import { useCartKeyContext } from '@/features/pos/components/Cart/contexts/CartKey'
import {
    getIsCartInFlightRequests,
    useCartOptimisticUpdateCounterActions,
} from '@/features/pos/components/Cart/hooks/cart-optimistic-update-counter'
import { BatchCallback } from '@/features/pos/components/Cart/hooks/useChangeCartItemQuantity/types'
import { useBatch } from '@/features/pos/components/Cart/hooks/useChangeCartItemQuantity/useBatch'
import { updateItemQuantityCache } from '@/features/pos/components/Cart/hooks/useChangeCartItemQuantity/utils'
import { cartItemChangeQuantityMutation } from '@/features/pos/components/Cart/mutations'
import {
    getCartFromCache,
    writeCartItemsToCache,
} from '@/features/pos/components/Cart/utils/cart-cache'
import { findCartItemWithIndex } from '@/features/pos/components/Cart/utils/cart-item'
import { logger } from '@/features/pos/logger'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useApolloClient, useMutation } from '@apollo/client'
import { useCallback } from 'react'
import invariant from 'tiny-invariant'

export const useChangeCartItemQuantity = (): ((id: string, quantityChange: number) => void) => {
    const handleError = useHandleErrorWithToast(logger)

    const [changeQuantity] = useMutation(cartItemChangeQuantityMutation, {
        fetchPolicy: 'no-cache',
        onError: handleError,
    })

    const { cartKey } = useCartKeyContext()

    const client = useApolloClient()

    const { increment, lock, reset } = useCartOptimisticUpdateCounterActions()

    const batchCallback = useCallback<BatchCallback>(
        (id, pendingQuantity, onCompleted, onError) => {
            invariant(cartKey, "'cartKey' must be set")

            lock(id)

            changeQuantity({
                variables: {
                    cartKey,
                    changeQuantity: { cartItemUuid: id, quantityChange: pendingQuantity },
                },
                onCompleted: (data) => {
                    onCompleted()

                    reset(id)

                    if (getIsCartInFlightRequests() || !data.cartItemChangeQuantity) return

                    writeCartItemsToCache(client.cache, cartKey, data.cartItemChangeQuantity.items)
                },
                onError: (error) => {
                    handleError(error)

                    reset(id)

                    onError((quantityChange) =>
                        updateItemQuantityCache(client, id, -quantityChange)
                    )
                },
            })
        },
        [cartKey, changeQuantity, client, handleError, lock, reset]
    )

    const handleExecuteEffect = useCallback(
        (id: string, quantityChange: number) => {
            increment(id)

            updateItemQuantityCache(client, id, quantityChange)
        },
        [client, increment]
    )

    const skip = useCallback(
        (id: string, quantityChange: number) => {
            invariant(cartKey, "'cartKey' must be set")

            const currentCart = getCartFromCache(client.cache, cartKey)

            const foundItem = findCartItemWithIndex(currentCart?.items, id)

            return !foundItem || foundItem.item.quantity + quantityChange < 1
        },
        [client, cartKey]
    )

    const handleNoPendingQuantity = useCallback(
        (id: string) => {
            lock(id)
            reset(id)
        },
        [lock, reset]
    )

    return useBatch(batchCallback, handleExecuteEffect, skip, handleNoPendingQuantity)
}
